import { Button } from '@/components/ui/button'
import {
  initializeStorefrontPreview,
  useDesignPreviewSession,
} from '@/lib/services/designs/designs.query'
import { cn } from '@/lib/utils'
import { useCallback, useEffect, useEffectEvent, useMemo, useRef, useState } from 'react'
import {
  buildStorefrontPreviewUrl,
  buildStorefrontPreviewUrlForPublicPath,
  composeStorefrontPreviewDesign,
  getStorefrontPreviewDraftSyncOptions,
  getStorefrontPreviewPageKeyFromPath,
  getStorefrontPreviewPublicPath,
  isStorefrontPreviewPageKey,
  resolveStorefrontPreviewBaseUrl,
  storefrontPreviewPages,
} from './storefrontPreviewConfig'
import {
  STOREFRONT_PREVIEW_DEVICES,
  StorefrontPreviewMessage,
  StorefrontPreviewPanel,
  StorefrontPreviewViewport,
} from './StorefrontPreviewPanel'

const STOREFRONT_PREVIEW_MESSAGE_TYPES = {
  navigate: 'tukios.storefront-preview.navigate',
  ready: 'tukios.storefront-preview.ready',
  routeChanged: 'tukios.storefront-preview.route-changed',
  sectionRequested: 'tukios.storefront-preview.section-requested',
  syncDraft: 'tukios.storefront-preview.sync-draft',
  syncFailed: 'tukios.storefront-preview.sync-failed',
  synced: 'tukios.storefront-preview.synced',
}

const STOREFRONT_PREVIEW_DRAFT_SYNC_DEBOUNCE_MS = 120
const MAX_TRACKED_PREVIEW_REVISIONS = 20

function shouldRetryPreviewSession(failureCount, error) {
  if (error?.response?.status === 404) {
    return false
  }

  return failureCount < 1
}

function hashPreviewSignature(signature) {
  if (typeof signature !== 'string' || signature.length === 0) {
    return ''
  }

  let hash = 0

  for (let index = 0; index < signature.length; index += 1) {
    hash = (hash * 31 + signature.charCodeAt(index)) | 0
  }

  return `${signature.length.toString(36)}-${(hash >>> 0).toString(36)}`
}

function normalizePreviewRevision(value) {
  const revision = Number(value)

  return Number.isSafeInteger(revision) && revision > 0 ? revision : 0
}

function createPreviewClientInstance() {
  return {
    id:
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `preview-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
    startedAtMs: Date.now(),
  }
}

function PreviewPageTabs({ previewPage, onPreviewPageChange }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {storefrontPreviewPages.map((page) => (
        <Button
          key={page.id}
          type="button"
          size="sm"
          variant={previewPage === page.id ? 'selected' : 'ghost'}
          className={cn(
            'h-8 rounded-full border border-white/10 px-3',
            previewPage === page.id
              ? 'border-transparent text-action-foreground hover:text-action-foreground'
              : 'text-white hover:bg-white/10 hover:text-white'
          )}
          onClick={() => onPreviewPageChange(page.id)}
        >
          <page.icon className="size-4" />
          {page.label}
        </Button>
      ))}
    </div>
  )
}

export default function StorefrontPreviewFrame({
  organization,
  design,
  defaultDesign = null,
  themes = [],
  collections = [],
  products = [],
  sameDayDeliveryPromotionEnabled,
  previewPage,
  onPreviewPageChange,
  previewDevice,
  onPreviewDeviceChange,
  onSectionRequest = null,
  syncDraft = true,
  className = null,
  viewportClassName = null,
}) {
  const iframeRef = useRef(null)
  const previewOriginRef = useRef(null)
  const pendingSyncSignatureRef = useRef(null)
  const pendingDraftSyncRef = useRef(null)
  const draftSyncTimerRef = useRef(null)
  const draftSyncFrameRef = useRef(null)
  const draftRevisionRef = useRef(0)
  const acknowledgedDraftRevisionRef = useRef(0)
  const sentDraftsRef = useRef(new Map())
  const syncedPreviewDesignRef = useRef(null)
  const syncedSameDayDeliveryPromotionEnabledRef = useRef(null)
  const currentPublicPathRef = useRef(getStorefrontPreviewPublicPath(previewPage))
  const currentPreviewPageRef = useRef(previewPage)
  const [previewClientInstance] = useState(createPreviewClientInstance)
  const previewBaseUrl = useMemo(
    () => resolveStorefrontPreviewBaseUrl(organization),
    [organization]
  )
  const previewHost = useMemo(() => {
    if (!previewBaseUrl) {
      return null
    }

    try {
      return new URL(previewBaseUrl).host || null
    } catch {
      return null
    }
  }, [previewBaseUrl])
  const shouldUsePreviewSession = Boolean(previewBaseUrl && previewHost)
  const previewSessionQuery = useDesignPreviewSession({
    organizationId: organization?.id ?? design?.organization_id,
    designId: design?.id,
    previewHost,
    enabled: shouldUsePreviewSession,
    refetchOnWindowFocus: false,
    retry: shouldRetryPreviewSession,
  })
  const previewToken = previewSessionQuery.data?.token ?? null
  const previewWriteKey = previewSessionQuery.data?.write_key ?? null
  const previewPublicPath = useMemo(
    () => getStorefrontPreviewPublicPath(previewPage),
    [previewPage]
  )
  const previewDesign = composeStorefrontPreviewDesign(
    design,
    defaultDesign,
    themes,
    collections,
    products
  )
  const previewDesignSignature = previewDesign
    ? JSON.stringify({
        design: previewDesign,
        sameDayDeliveryPromotionEnabled,
      })
    : null
  const previewUrl = useMemo(
    () =>
      buildStorefrontPreviewUrl({
        baseUrl: previewBaseUrl,
        previewToken,
        previewPage,
      }),
    [previewBaseUrl, previewPage, previewToken]
  )
  const [frameSrc, setFrameSrc] = useState(previewUrl)
  const frameSrcRef = useRef(previewUrl)
  const [frameVersion, setFrameVersion] = useState(0)
  const [loadedFrameSrc, setLoadedFrameSrc] = useState(null)
  const initializedPreviewSessionKeyRef = useRef(null)
  const previewSessionInitPayloadRef = useRef(null)
  const [readyPreviewSessionKey, setReadyPreviewSessionKey] = useState(null)
  const [previewSessionInitErrorState, setPreviewSessionInitErrorState] = useState({
    key: null,
    message: null,
  })
  const [previewSyncError, setPreviewSyncError] = useState(null)
  const [syncedSignature, setSyncedSignature] = useState(
    syncDraft === false ? 'live-preview' : null
  )
  const previewSessionInitKey = useMemo(() => {
    if (!previewToken || !previewWriteKey || !previewHost || !previewDesignSignature) {
      return null
    }

    return syncDraft
      ? `${previewToken}:${previewHost}:${previewClientInstance.id}`
      : `${previewToken}:${previewHost}:${previewDesignSignature}`
  }, [
    previewClientInstance.id,
    previewDesignSignature,
    previewHost,
    previewToken,
    previewWriteKey,
    syncDraft,
  ])
  const hasPreviewSessionCredentials = Boolean(previewToken && previewWriteKey)
  const previewSessionInitError =
    previewSessionInitErrorState.key === previewSessionInitKey
      ? previewSessionInitErrorState.message
      : null
  const isPreviewSessionReady =
    hasPreviewSessionCredentials && readyPreviewSessionKey === previewSessionInitKey
  const isPreviewSynced =
    syncDraft === false || !previewDesignSignature || previewDesignSignature === syncedSignature
  const isFrameReady = loadedFrameSrc === frameSrc
  const showSyncStatus = syncDraft && isFrameReady && !isPreviewSynced && !previewSyncError
  const previewStatus = previewSessionQuery.isError
    ? { state: 'error', label: 'Preview unavailable' }
    : previewSessionInitError
      ? { state: 'error', label: 'Preview unavailable' }
      : !isPreviewSessionReady
        ? { state: 'loading', label: 'Preparing preview' }
        : !isFrameReady
          ? { state: 'loading', label: 'Loading storefront' }
          : previewSyncError
            ? { state: 'error', label: previewSyncError.label }
            : showSyncStatus
              ? { state: 'syncing', label: 'Updating preview' }
              : { state: 'ready', label: syncDraft ? 'Live preview' : 'Storefront preview' }

  const cancelScheduledDraftSync = useEffectEvent(() => {
    if (draftSyncTimerRef.current) {
      window.clearTimeout(draftSyncTimerRef.current)
      draftSyncTimerRef.current = null
    }

    if (draftSyncFrameRef.current) {
      window.cancelAnimationFrame(draftSyncFrameRef.current)
      draftSyncFrameRef.current = null
    }
  })

  const flushQueuedDraftToPreview = useEffectEvent(() => {
    const pendingDraft = pendingDraftSyncRef.current

    if (
      !syncDraft ||
      !previewWriteKey ||
      (!isFrameReady && !pendingDraft?.allowBeforeReady) ||
      !pendingDraft?.design ||
      !pendingDraft?.signature ||
      pendingDraft.signature === syncedSignature ||
      !iframeRef.current?.contentWindow
    ) {
      return
    }

    const revision = draftRevisionRef.current + 1
    const draftSyncOptions = getStorefrontPreviewDraftSyncOptions(
      syncedPreviewDesignRef.current,
      pendingDraft.design
    )
    const reloadRequired =
      draftSyncOptions.reloadRequired ||
      pendingDraft.sameDayDeliveryPromotionEnabled !==
        syncedSameDayDeliveryPromotionEnabledRef.current
    const { patchTargets } = draftSyncOptions
    draftRevisionRef.current = revision
    pendingSyncSignatureRef.current = pendingDraft.signature
    pendingDraftSyncRef.current = null

    sentDraftsRef.current.set(revision, {
      signature: pendingDraft.signature,
      signatureHash: pendingDraft.signatureHash,
      design: pendingDraft.design,
      sameDayDeliveryPromotionEnabled: pendingDraft.sameDayDeliveryPromotionEnabled,
      reloadRequired,
      patchTargets,
    })

    while (sentDraftsRef.current.size > MAX_TRACKED_PREVIEW_REVISIONS) {
      const oldestRevision = sentDraftsRef.current.keys().next().value
      sentDraftsRef.current.delete(oldestRevision)
    }

    iframeRef.current.contentWindow.postMessage(
      {
        type: STOREFRONT_PREVIEW_MESSAGE_TYPES.syncDraft,
        token: previewToken,
        writeKey: previewWriteKey,
        revision,
        signature: pendingDraft.signatureHash,
        reloadRequired,
        patchTargets,
        design: pendingDraft.design,
        sameDayDeliveryPromotionEnabled: pendingDraft.sameDayDeliveryPromotionEnabled,
        clientInstanceId: previewClientInstance.id,
        clientInstanceStartedAtMs: previewClientInstance.startedAtMs,
      },
      previewOriginRef.current || '*'
    )
  })

  const queueDraftToPreview = useEffectEvent(
    ({
      design: nextDesign,
      sameDayDeliveryPromotionEnabled: nextSameDayDeliveryPromotionEnabled,
      signature,
      immediate,
      allowBeforeReady,
    }) => {
      if (
        !syncDraft ||
        !isPreviewSessionReady ||
        (!isFrameReady && !allowBeforeReady) ||
        !nextDesign ||
        !signature ||
        signature === syncedSignature ||
        signature === pendingSyncSignatureRef.current ||
        signature === pendingDraftSyncRef.current?.signature
      ) {
        return
      }

      pendingDraftSyncRef.current = {
        design: nextDesign,
        sameDayDeliveryPromotionEnabled: nextSameDayDeliveryPromotionEnabled,
        signature,
        signatureHash: hashPreviewSignature(signature),
        allowBeforeReady: Boolean(allowBeforeReady),
      }
      setPreviewSyncError(null)

      cancelScheduledDraftSync()

      if (immediate) {
        flushQueuedDraftToPreview()
        return
      }

      draftSyncTimerRef.current = window.setTimeout(() => {
        draftSyncTimerRef.current = null
        draftSyncFrameRef.current = window.requestAnimationFrame(() => {
          draftSyncFrameRef.current = null
          flushQueuedDraftToPreview()
        })
      }, STOREFRONT_PREVIEW_DRAFT_SYNC_DEBOUNCE_MS)
    }
  )

  useEffect(() => {
    if (!previewSessionInitKey || !previewDesign || !previewDesignSignature) {
      previewSessionInitPayloadRef.current = null
      return
    }

    previewSessionInitPayloadRef.current = {
      key: previewSessionInitKey,
      design: previewDesign,
      sameDayDeliveryPromotionEnabled,
      signature: previewDesignSignature,
      clientInstanceId: previewClientInstance.id,
      clientInstanceStartedAtMs: previewClientInstance.startedAtMs,
    }
  }, [
    previewClientInstance.id,
    previewClientInstance.startedAtMs,
    previewDesign,
    previewDesignSignature,
    previewSessionInitKey,
    sameDayDeliveryPromotionEnabled,
  ])

  useEffect(() => {
    if (
      !previewSessionInitKey ||
      !previewBaseUrl ||
      !previewToken ||
      !previewWriteKey ||
      !previewHost
    ) {
      return
    }

    if (initializedPreviewSessionKeyRef.current === previewSessionInitKey) {
      return
    }

    const previewSessionInitPayload = previewSessionInitPayloadRef.current

    if (!previewSessionInitPayload || previewSessionInitPayload.key !== previewSessionInitKey) {
      return
    }

    const abortController = new AbortController()
    let cancelled = false

    void initializeStorefrontPreview({
      previewBaseUrl,
      writeKey: previewWriteKey,
      token: previewToken,
      signal: abortController.signal,
      payload: {
        token: previewToken,
        host: previewHost,
        design: previewSessionInitPayload.design,
        sameDayDeliveryPromotionEnabled: previewSessionInitPayload.sameDayDeliveryPromotionEnabled,
        signature: previewSessionInitPayload.signature,
        clientInstanceId: previewSessionInitPayload.clientInstanceId,
        clientInstanceStartedAtMs: previewSessionInitPayload.clientInstanceStartedAtMs,
        updatedAt: new Date().toISOString(),
      },
    })
      .then(() => {
        if (cancelled) {
          return
        }

        initializedPreviewSessionKeyRef.current = previewSessionInitKey
        syncedPreviewDesignRef.current = previewSessionInitPayload.design
        syncedSameDayDeliveryPromotionEnabledRef.current =
          previewSessionInitPayload.sameDayDeliveryPromotionEnabled
        setReadyPreviewSessionKey(previewSessionInitKey)
        if (syncDraft) {
          setSyncedSignature(previewSessionInitPayload.signature)
        }
      })
      .catch((error) => {
        if (cancelled || error?.name === 'AbortError') {
          return
        }

        setPreviewSessionInitErrorState({
          key: previewSessionInitKey,
          message:
            error instanceof Error && error.message.trim() !== ''
              ? error.message
              : 'Unable to start the storefront preview.',
        })
      })

    return () => {
      cancelled = true
      abortController.abort()
    }
  }, [previewBaseUrl, previewHost, previewSessionInitKey, previewToken, previewWriteKey, syncDraft])

  const handlePreviewPageButtonChange = useCallback(
    (nextPreviewPage) => {
      onPreviewPageChange(nextPreviewPage)

      const nextPublicPath = getStorefrontPreviewPublicPath(nextPreviewPage)

      if (isFrameReady && iframeRef.current?.contentWindow) {
        currentPublicPathRef.current = nextPublicPath
        iframeRef.current.contentWindow.postMessage(
          {
            type: STOREFRONT_PREVIEW_MESSAGE_TYPES.navigate,
            token: previewToken,
            publicPath: nextPublicPath,
          },
          previewOriginRef.current || '*'
        )
        return
      }

      const nextFrameSrc = buildStorefrontPreviewUrl({
        baseUrl: previewBaseUrl,
        previewToken,
        previewPage: nextPreviewPage,
      })

      if (!nextFrameSrc) {
        return
      }

      currentPublicPathRef.current = nextPublicPath
      setFrameSrc(nextFrameSrc)
      setLoadedFrameSrc(null)
      setFrameVersion((version) => version + 1)
    },
    [isFrameReady, onPreviewPageChange, previewBaseUrl, previewToken]
  )

  const handlePreviewMessage = useEffectEvent((event) => {
    if (!previewBaseUrl) {
      return
    }

    if (event.origin !== previewOriginRef.current) {
      return
    }

    const { type, token, pageKey, publicPath, sectionKey, revision, signature } = event.data ?? {}

    if (token !== previewToken) {
      return
    }

    if (type === STOREFRONT_PREVIEW_MESSAGE_TYPES.ready) {
      const currentFrameWindow = iframeRef.current?.contentWindow

      if (currentFrameWindow && event.source && event.source !== currentFrameWindow) {
        return
      }

      const queuedDraft = pendingDraftSyncRef.current

      setLoadedFrameSrc(frameSrcRef.current)

      if (
        queuedDraft?.design &&
        queuedDraft.signature &&
        queuedDraft.signature !== syncedSignature
      ) {
        pendingDraftSyncRef.current = {
          ...queuedDraft,
          allowBeforeReady: true,
        }
        flushQueuedDraftToPreview()
      }

      return
    }

    if (type === STOREFRONT_PREVIEW_MESSAGE_TYPES.synced) {
      const syncedRevision = normalizePreviewRevision(revision)

      if (syncedRevision > 0) {
        if (
          syncedRevision < draftRevisionRef.current ||
          syncedRevision <= acknowledgedDraftRevisionRef.current
        ) {
          return
        }

        const sentDraft = sentDraftsRef.current.get(syncedRevision)
        const syncedSignatureHash = typeof signature === 'string' ? signature : ''

        if (
          sentDraft &&
          (!syncedSignatureHash || syncedSignatureHash === sentDraft.signatureHash)
        ) {
          acknowledgedDraftRevisionRef.current = syncedRevision
          sentDraftsRef.current.delete(syncedRevision)
          if (pendingSyncSignatureRef.current === sentDraft.signature) {
            pendingSyncSignatureRef.current = null
          }
          syncedPreviewDesignRef.current = sentDraft.design
          syncedSameDayDeliveryPromotionEnabledRef.current =
            sentDraft.sameDayDeliveryPromotionEnabled
          setPreviewSyncError(null)
          setSyncedSignature(sentDraft.signature)

          if (sentDraft.reloadRequired) {
            setLoadedFrameSrc(null)
          }
        }

        return
      }

      if (syncDraft && draftRevisionRef.current > 0) {
        return
      }

      setPreviewSyncError(null)
      setSyncedSignature(
        pendingSyncSignatureRef.current ?? previewDesignSignature ?? 'live-preview'
      )
      return
    }

    if (type === STOREFRONT_PREVIEW_MESSAGE_TYPES.syncFailed) {
      const failedRevision = normalizePreviewRevision(revision)

      if (failedRevision > 0 && failedRevision < draftRevisionRef.current) {
        sentDraftsRef.current.delete(failedRevision)
        return
      }

      const shouldReportFailure =
        failedRevision <= 0 || failedRevision > acknowledgedDraftRevisionRef.current

      if (shouldReportFailure) {
        const shouldClearPendingSignature =
          failedRevision <= 0 || failedRevision >= draftRevisionRef.current

        if (shouldClearPendingSignature) {
          pendingSyncSignatureRef.current = null
        }

        if (failedRevision > 0) {
          sentDraftsRef.current.delete(failedRevision)
        }
        setSyncedSignature(null)
        setPreviewSyncError({
          revision: failedRevision,
          label: 'Preview update failed',
        })
      }

      return
    }

    if (type === STOREFRONT_PREVIEW_MESSAGE_TYPES.routeChanged) {
      const nextPreviewPage = isStorefrontPreviewPageKey(pageKey)
        ? pageKey
        : getStorefrontPreviewPageKeyFromPath(publicPath)

      currentPublicPathRef.current =
        publicPath || getStorefrontPreviewPublicPath(nextPreviewPage || previewPage)
      currentPreviewPageRef.current = nextPreviewPage || currentPreviewPageRef.current

      if (nextPreviewPage && nextPreviewPage !== previewPage) {
        onPreviewPageChange(nextPreviewPage)
      }

      return
    }

    if (type === STOREFRONT_PREVIEW_MESSAGE_TYPES.sectionRequested && sectionKey) {
      onSectionRequest?.(sectionKey)
    }
  })

  useEffect(() => {
    previewOriginRef.current = previewBaseUrl ? new URL(previewBaseUrl).origin : null
  }, [previewBaseUrl])

  useEffect(() => {
    frameSrcRef.current = frameSrc
  }, [frameSrc])

  useEffect(() => {
    window.addEventListener('message', handlePreviewMessage)

    return () => {
      window.removeEventListener('message', handlePreviewMessage)
    }
  }, [])

  useEffect(() => {
    if (!previewDesign || !previewDesignSignature) {
      return
    }

    queueDraftToPreview({
      design: previewDesign,
      sameDayDeliveryPromotionEnabled,
      signature: previewDesignSignature,
    })
  }, [
    isFrameReady,
    previewDesign,
    previewDesignSignature,
    sameDayDeliveryPromotionEnabled,
    syncDraft,
    syncedSignature,
  ])

  useEffect(
    () => () => {
      cancelScheduledDraftSync()
    },
    []
  )

  const resetFrameToCurrentPreview = useEffectEvent(() => {
    cancelScheduledDraftSync()
    initializedPreviewSessionKeyRef.current = null
    pendingDraftSyncRef.current = null
    pendingSyncSignatureRef.current = null
    sentDraftsRef.current.clear()
    syncedPreviewDesignRef.current = null
    syncedSameDayDeliveryPromotionEnabledRef.current = null
    draftRevisionRef.current = 0
    acknowledgedDraftRevisionRef.current = 0
    setReadyPreviewSessionKey(null)
    setPreviewSessionInitErrorState({ key: null, message: null })
    setPreviewSyncError(null)
    setFrameSrc(previewUrl)
    setLoadedFrameSrc(null)
    currentPublicPathRef.current = previewPublicPath
    currentPreviewPageRef.current = previewPage
    setSyncedSignature(syncDraft === false ? 'live-preview' : null)
  })

  useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect -- grandfathered
    resetFrameToCurrentPreview()
  }, [previewBaseUrl, previewToken, syncDraft])

  const syncPendingFrameSrc = useEffectEvent(() => {
    if (!previewUrl) {
      setFrameSrc(null)
      setLoadedFrameSrc(null)
      return
    }

    if (!loadedFrameSrc) {
      const pendingPublicPath = currentPublicPathRef.current || previewPublicPath
      const pendingFrameSrc =
        buildStorefrontPreviewUrlForPublicPath({
          baseUrl: previewBaseUrl,
          previewToken,
          publicPath: pendingPublicPath,
        }) ?? previewUrl

      setFrameSrc(pendingFrameSrc)
      currentPublicPathRef.current = pendingPublicPath
    }
  })

  useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect -- grandfathered
    syncPendingFrameSrc()
  }, [loadedFrameSrc, previewPublicPath, previewUrl])

  useEffect(() => {
    if (!previewBaseUrl) {
      return
    }

    const currentPreviewPage = getStorefrontPreviewPageKeyFromPath(currentPublicPathRef.current)
    const effectiveCurrentPreviewPage = currentPreviewPage || currentPreviewPageRef.current

    if (
      !isFrameReady ||
      !iframeRef.current?.contentWindow ||
      effectiveCurrentPreviewPage === previewPage
    ) {
      return
    }

    iframeRef.current.contentWindow.postMessage(
      {
        type: STOREFRONT_PREVIEW_MESSAGE_TYPES.navigate,
        token: previewToken,
        publicPath: previewPublicPath,
      },
      previewOriginRef.current || '*'
    )
  }, [isFrameReady, previewBaseUrl, previewPage, previewPublicPath, previewToken])

  if (!previewBaseUrl) {
    return (
      <StorefrontPreviewPanel
        status={previewStatus}
        view={previewDevice}
        views={STOREFRONT_PREVIEW_DEVICES}
        onViewChange={onPreviewDeviceChange}
        pageTabs={
          <PreviewPageTabs
            previewPage={previewPage}
            onPreviewPageChange={handlePreviewPageButtonChange}
          />
        }
        className={className}
      >
        <StorefrontPreviewMessage title="Preview not available yet">
          Add a demo storefront URL for this account to see the real storefront here.
        </StorefrontPreviewMessage>
      </StorefrontPreviewPanel>
    )
  }

  if (!frameSrc || !isPreviewSessionReady) {
    return (
      <StorefrontPreviewPanel
        status={previewStatus}
        view={previewDevice}
        views={STOREFRONT_PREVIEW_DEVICES}
        onViewChange={onPreviewDeviceChange}
        pageTabs={
          <PreviewPageTabs
            previewPage={previewPage}
            onPreviewPageChange={handlePreviewPageButtonChange}
          />
        }
        className={className}
      >
        {previewSessionQuery.isError ? (
          <StorefrontPreviewMessage title="Preview temporarily unavailable">
            We couldn&apos;t prepare the storefront preview just now. Refresh and try again.
          </StorefrontPreviewMessage>
        ) : previewSessionInitError ? (
          <StorefrontPreviewMessage title="Preview temporarily unavailable">
            {previewSessionInitError || "We couldn't start the storefront preview just now."}
          </StorefrontPreviewMessage>
        ) : (
          <StorefrontPreviewMessage title="Preparing storefront preview">
            Starting the storefront preview session for this account.
          </StorefrontPreviewMessage>
        )}
      </StorefrontPreviewPanel>
    )
  }

  return (
    <StorefrontPreviewPanel
      status={previewStatus}
      view={previewDevice}
      views={STOREFRONT_PREVIEW_DEVICES}
      onViewChange={onPreviewDeviceChange}
      pageTabs={
        <PreviewPageTabs
          previewPage={previewPage}
          onPreviewPageChange={handlePreviewPageButtonChange}
        />
      }
      className={className}
    >
      <StorefrontPreviewViewport
        device={previewDevice}
        isLoading={!isFrameReady}
        className={viewportClassName}
      >
        <iframe
          key={frameVersion}
          ref={iframeRef}
          src={frameSrc}
          title="Storefront preview"
          className="size-full border-0 bg-white"
          onLoad={() => {
            setLoadedFrameSrc(frameSrcRef.current)
          }}
        />
      </StorefrontPreviewViewport>
    </StorefrontPreviewPanel>
  )
}
